(ns app.front.view.admin.gis.search-config
  "GIS search configuration for ArcGIS, CGIS, or WFS services."
  (:require ["@carbon/icons-react/es/password/16" :default Password16]
            [app.domain.group.model :as group-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.common :as admin-common]
            [app.front.view.admin.gis.arcgis-search-config :as arcgis-search-config]
            [app.front.view.admin.gis.common :as gis-common]
            [app.front.view.admin.gis.subs :as gis-subs]
            [app.front.view.workspace.map :as workspace-map]
            [app.gis.config-model :as gis-config-model]
            [app.gis.util :as gis-util]
            [cljs.pprint]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [core.gis.arcgis.model :as arcgis-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def roles-for-system
  {:arcgis        [:address-search
                   :point-search
                   :property-owner-search
                   :neighbour-search]
   :azimut-arcgis [:address-search
                   :point-search
                   :property-owner-search
                   :neighbour-search]
   :regrid-arcgis [:address-search
                   :point-search
                   :property-owner-search
                   :neighbour-search]
   :cgis          [:address-search
                   :property-owner-search]
   :wms-wfs       [:address-search
                   :point-search
                   :property-owner-search]})


(defn set-default-cgis-mapping
  "CGIS seems to have a pretty established set of attributes, so map those out of convenience."
  [config]
  (update config :gis-search-config/mapping (fnil identity {:property/id             "roll_number"
                                                            :address/full-address    "civic_address"
                                                            :property/integration-id "roll_number"
                                                            :property/type-code      "prop_code"
                                                            :property/legal          "legal_description"
                                                            :property/zoning         "zoning"})))


(defn set-default-wfs-mapping
  "WFS has some read-only attribute mappings"
  [config]
  (-> config
      (update :gis-search-config/mapping
              (fnil identity gis-config-model/wfs-constant-attribute-mappings))))

(rf/reg-event-db ::add-municipality-filter
  (fn [db [_ server-id config-id]]
    (update-in db
               [::search-config-editors server-id config-id :gis-search-config/municipality-filters]
               (comp vec concat)
               [{:gis-municipality-filter/municipality-number nil
                 :gis-municipality-filter/attribute           nil
                 :gis-municipality-filter/attribute-type      nil
                 :gis-municipality-filter/value               nil
                 :gis-municipality-filter/match-type          :equals}])))


(rf/reg-event-db ::set-municipality-filter-value
  (fn [db [_ server-id config-id idx partial-config]]
    (update-in db
               [::search-config-editors server-id config-id :gis-search-config/municipality-filters idx]
               merge
               partial-config)))


(rf/reg-event-db ::remove-municipality-filter
  (fn [db [_ server-id config-id idx]]
    (update-in db
               [::search-config-editors server-id config-id :gis-search-config/municipality-filters]
               (fn [filters]
                 (let [[before after] (split-at idx filters)]
                   (vec (concat before (rest after))))))))


(rf/reg-event-fx ::open-config-editor
  (fn [{:keys [db]} [_ server-config initial-data group-ids]]
    (let [server-id    (:db/id server-config)
          arcgis?      (or (gis-common/arcgis-server? server-config)
                           (gis-common/system-of-type? server-config :azimut-arcgis :regrid-arcgis))
          cgis?        (gis-common/cgis-server? server-config)
          wfs?         (gis-common/wms-wfs-server? server-config)
          trimmed-data (->> (cond
                              arcgis? (st/required-keys gis-config-model/ArcGISSearchConfig)
                              cgis? (st/required-keys gis-config-model/CGISSearchConfig)
                              :else (st/required-keys gis-config-model/WFSSearchConfig))
                            keys
                            (select-keys initial-data))]
      (merge
        {:db (->> (cond-> trimmed-data
                    (or arcgis? wfs?) (update :gis-search-config/municipality-filters
                                              (fn [filters]
                                                (->> filters
                                                     (mapv #(->> gis-config-model/GisMunicipalityFilter
                                                                 st/required-keys ; takes care of optionals
                                                                 keys
                                                                 (select-keys %))))))
                    arcgis? (update :gis-service-config/arcgis-folder str)
                    cgis? set-default-cgis-mapping
                    wfs? set-default-wfs-mapping)
                  (merge {:group-ids group-ids})
                  (assoc-in db [::search-config-editors server-id (:db/id initial-data :new)]))}
        (when cgis?
          ;; CGIS has no services/folders/layers to select, so fetch the sample immediately.
          {:dispatch [::gis-common/fetch-cgis-sample server-id]})))))


(rf/reg-event-db ::close-config-editor
  (fn [db [_ server-id config-id]]
    (p/dissoc-in db [::search-config-editors server-id config-id])))


(rf/reg-sub ::config-in-editor
  (fn [db [_ server-id config-id]]
    (get-in db [::search-config-editors server-id config-id])))


(rf/reg-sub ::relevant-municipalities
  (fn [[_ server-id config-id] _]
    [(rf/subscribe [::config-in-editor server-id config-id])
     (rf/subscribe [::admin-common/domain-groups-of-authorities])])
  (fn [[config domain-groups] _]
    (->> domain-groups
         (filter #(contains? (set (:group-ids config)) (:db/id %)))
         (mapcat :entity/_parent)
         (mapcat group-model/scope-municipalities)
         distinct
         (sort-by :municipality/number))))

(rf/reg-sub ::relevant-municipalities-with-center
  (fn [[_ server-id config-id]]
    (rf/subscribe [::relevant-municipalities server-id config-id]))
  (fn [municipalities _]
    (filter :municipality/center-location municipalities)))



(rf/reg-event-fx ::fetch-wfs-capabilities
  (fn [{:keys [db]} [_ server-id path search-config-id on-success-event]]
    {:db           (-> db
                       (assoc-in [::wfs-capabilities server-id search-config-id] {:loading true})
                       (assoc-in [::wfs-feature-sample server-id search-config-id] nil))
     :comm/command {:command    :admin/fetch-wfs-capabilities
                    :data       {:gis-server-id server-id
                                 :path          (or path "")}
                    :on-success [::fetch-wfs-capabilities.success server-id search-config-id on-success-event]}}))


(rf/reg-event-fx ::debounce-fetch-wfs-capabilities
  (fn [{:keys [db]} [_ server-id path search-config-id]]
    {:db                (-> db
                            (assoc-in [::wfs-capabilities server-id search-config-id] {:loading true})
                            (assoc-in [::wfs-feature-sample server-id search-config-id] nil))
     :dispatch-debounce {:id      ::wfs-capabilities-fetch
                         :timeout 800
                         :action  :dispatch
                         :event   [::fetch-wfs-capabilities server-id path search-config-id]}}))


(rf/reg-event-fx ::fetch-wfs-capabilities.success
  (fn [{:keys [db]} [_ server-id search-config-id on-success-event _status response _request-id]]
    (p/assoc-if
      {:db (-> db
               (assoc-in
                 [::wfs-capabilities server-id search-config-id]
                 response)
               (assoc-in
                 [::search-config-editors server-id search-config-id :gis-search-config/wfs-version]
                 (:service-version response)))}
      :dispatch on-success-event)))


(rf/reg-sub ::wfs-capabilities
  (fn [db [_ server-id search-config-id]]
    (get-in db [::wfs-capabilities server-id search-config-id])))


(rf/reg-event-fx ::fetch-wfs-sample
  (fn [{:keys [db]} [_ server-id search-config-id version path feature-name]]
    {:db           (-> db (assoc-in [::wfs-feature-sample server-id search-config-id] {:loading true}))
     :comm/command {:command    :admin/fetch-wfs-example
                    :data       {:gis-server-id   server-id
                                 :service-version version
                                 :path            (or path "")
                                 :feature-name    feature-name}
                    :on-success [::fetch-wfs-sample.success server-id search-config-id]}}))


(rf/reg-event-db ::fetch-wfs-sample.success
  (fn [db [_ server-id search-config-id _status response _request-id]]
    (-> db
        (assoc-in
          [::wfs-feature-sample server-id search-config-id]
          response)
        (assoc-in
          [::search-config-editors server-id search-config-id :gis-search-config/wfs-feature-crs]
          (-> response :crs-name)))))


(rf/reg-sub ::wfs-feature-sample
  (fn [db [_ server-id search-config-id]]
    (get-in db [::wfs-feature-sample server-id search-config-id])))




(defn config-has-role? [config & roles]
  (->> config :gis-search-config/roles (some (set roles))))


(defn arcgis-search-config-checker [config]
  (s/checker (merge (st/required-keys gis-config-model/ArcGISSearchConfig
                                      (vec (concat (when (config-has-role? config :address-search :point-search)
                                                     [:gis-search-config/mapping])
                                                   (when (config-has-role? config :property-owner-search :neighbour-search)
                                                     [:gis-search-config/property-search-config]))))
                    {:group-ids #{s/Int}})))


(def cgis-search-config-checker (s/checker (merge gis-config-model/CGISSearchConfig
                                                  {:group-ids #{s/Int}})))


(def wfs-search-config-checker (s/checker (merge gis-config-model/WFSSearchConfig
                                                 {:group-ids #{s/Int}})))


(defn role-tag [tr role]
  [ui/tag {:type       :round
           :color      :blue
           :class-name "first:ml-0 cloudpermit--tag--round"}
   (tr [(keyword "gis-search-config.role" role)])])


(defn municipality-mapping [{:keys [tr server-id search-config-id gis-attribute-names attribute-types]}
                            {:gis-search-config/keys [municipality-filters]}]
  (let [municipalities (<sub [::relevant-municipalities server-id search-config-id])
        used-numbers   (->> municipality-filters
                            (keep :gis-municipality-filter/municipality-number)
                            set)
        lang           (<sub [:language])]
    [:div.mt-6
     [:h6.mb-3 (tr [:gis-search-config/municipality-filters])]
     [:p.py-1 (tr [:gis-search-config/municipality-filters-guide])]
     (for-frag [[i {:gis-municipality-filter/keys [municipality-number
                                                   attribute
                                                   attribute-type
                                                   value
                                                   match-type]}]
                (map-indexed vector municipality-filters)]
       [:div.border.border-solid.border-ui-violet-100.rounded.mt-3
        [:div.flex.w-full.justify-end.-mb-6
         (cc/button {:kind             :ghost
                     :icon-position    :right
                     :icon-description (tr [:cancel])
                     :class-name       "only-icon-button"
                     :size             :field
                     :icon             i/remove
                     :on-click         #(>evt [::remove-municipality-filter server-id search-config-id i])}
                    "")]
        [:div.flex.flex-wrap.px-4.pb-4
         [:div.w-full.md:w-1-2.md:pr-2.mt-3
          [ui-select/select {:id           (str/join "-" [server-id search-config-id "mmapping" i "municipality"])
                             :class-name   "full-width-select"
                             :select-text  (tr [:municipalities/select-municipality])
                             :label-text   (tr [:municipalities/municipality])
                             :value        municipality-number
                             :on-change    #(>evt [::set-municipality-filter-value
                                                   server-id
                                                   search-config-id
                                                   i
                                                   {:gis-municipality-filter/municipality-number %}])
                             :val->str     #(str (or (-> municipalities
                                                         (p/find-first {:municipality/number %})
                                                         (municipality-model/municipality-name lang))
                                                     %)
                                                 (when (contains? used-numbers %)
                                                   " ☆"))
                             :allow-empty? false
                             :values       (->> (map :municipality/number municipalities)
                                                (concat (when municipality-number
                                                          [municipality-number]))
                                                distinct
                                                sort)}]]
         [:div.w-full.md:w-1-2.md:pl-2.mt-3
          [ui-select/select {:id           (str/join "-" [server-id search-config-id "mmapping" i "gis-attr"])
                             :class-name   "full-width-select"
                             :select-text  (tr [:gis-municipality-filter/attribute-select])
                             :label-text   (tr [:gis-municipality-filter/attribute])
                             :value        attribute
                             :on-change    (fn [attr]
                                             (let [attr-type (get attribute-types attr)]
                                               (>evt [::set-municipality-filter-value
                                                      server-id
                                                      search-config-id
                                                      i
                                                      (merge
                                                        {:gis-municipality-filter/attribute      attr
                                                         :gis-municipality-filter/attribute-type attr-type}
                                                        (when (arcgis-model/number-field-type? attr-type)
                                                          {:gis-municipality-filter/match-type :equals}))])))
                             :val->str     identity
                             :allow-empty? false
                             :values       gis-attribute-names}]]
         [:div.w-full.md:w-1-2.md:pr-2.mt-3
          [ui-select/select {:id           (str/join "-" [server-id search-config-id "mmapping" i "gis-attr"])
                             :class-name   "full-width-select"
                             :label-text   (tr [:gis-municipality-filter/match-type])
                             :value        match-type
                             :on-change    #(>evt [::set-municipality-filter-value
                                                   server-id
                                                   search-config-id
                                                   i
                                                   {:gis-municipality-filter/match-type %}])
                             :val->str     name
                             :allow-empty? false
                             :values       (if (arcgis-model/number-field-type? attribute-type)
                                             [:equals]
                                             [:equals :prefix])}]]
         [:div.w-full.md:w-1-2.md:pl-2.mt-3
          [ui/text-input {:label-text (tr [:gis-municipality-filter/value])
                          :on-change  #(rf/dispatch-sync [::set-municipality-filter-value
                                                          server-id
                                                          search-config-id
                                                          i
                                                          {:gis-municipality-filter/value (futil/tval %)}])
                          :value      value}]]]])
     [:div.mt-3
      (cc/button {:size     :small
                  :icon     i/plus
                  :kind     :tertiary
                  :on-click #(>evt [::add-municipality-filter server-id search-config-id])}
                 (tr [:gis-municipality-filter/add-mapping]))]]))


(defn sample-attributes [tr gis-attributes sample]
  (if (seq gis-attributes)
    [:div.border.border-solid.border-ui-violet-100.rounded.p-4.overflow-x-auto
     [:div.font-semibold.mb-3 (tr [:gis-editor/attributes])]
     [:table
      (into
        [:tbody]
        (for [[k v] gis-attributes
              :when (not= k "geometry")]
          [:tr
           [:td.font-semibold.p-1.break-all
            {:style {:min-width "150px"}}
            [:div.flex.items-center
             [:span k]
             (when (= k (-> sample :id-field))
               [:span.ml-1
                (r/create-element Password16 #js{:aria-label "Primary key"})])]]
           [:td.p-1.whitespace-pre-wrap.font-mono (str v)]]))]
     (when (:geometry sample)
       [:<>
        [:div.font-semibold.my-3 (tr [:gis-editor/geometry])]
        [:div.font-mono
         [:pre (with-out-str (cljs.pprint/pprint (:geometry sample)))]]])]
    [:div "No record found."]))


(defn sample-record-map [{:keys [tr
                                 municipalities
                                 map-click-evt
                                 server-id
                                 search-config-id
                                 sample
                                 initially-collapsed?]}]
  (r/with-let [*selected-muni (r/atom nil)]
    (let [map-center    (or (<sub [::gis-common/forced-map-center server-id])
                            (-> sample :geometry-point)
                            ;; show map for clicks even if no sample found on current layer
                            (<sub [::gis-common/last-coordinate server-id]))
          property-geom (when-let [geometry (-> sample :geometry)]
                          (property-model/geojson-feature
                            geometry))
          lang          (<sub [:language])]
      [:div
       [section/subsection-2
        {:tr                   tr
         :title                "Map"
         :title-class          "font-semibold text-sm"
         :initially-collapsed? (boolean initially-collapsed?)}
        [:div.mb-6
         [:p (str (tr [:gis-editor/click-fetch-sample])
                  " "
                  (tr [(if (next municipalities)
                         :gis-editor/select-center-municipality
                         :gis-editor/select-domain-to-show-municipalities)]))]
         (when (seq municipalities)
           [:div.mb-3
            [ui-select/select {:id           (str/join "-" [server-id search-config-id "map-center-muni"])
                               :class-name   "full-width-select"
                               :select-text  (tr [:municipalities/select-municipality])
                               :label-text   (tr [:gis-editor/map-center-municipality])
                               :value        @*selected-muni
                               :on-change    (fn [{{:location.center/keys [x y]} :municipality/center-location :as v}]
                                               (reset! *selected-muni v)
                                               (>evt [::gis-common/force-map-center
                                                      server-id
                                                      [x y]]))
                               :val->str     #(municipality-model/municipality-name % lang)
                               :allow-empty? false
                               :values       municipalities}]])
         (when map-center
           [:div.w-full.h-80
            [workspace-map/workspace-map {:center             map-center
                                          :primaryMapMarkers  (some-> sample :geometry-point vector)
                                          :propertyGeometries {:primaryPropertyGeometry property-geom}
                                          :events             [{:eventType     "singleclick"
                                                                :eventFunction (fn [^js e]
                                                                                 (reset! *selected-muni nil)
                                                                                 (when map-click-evt
                                                                                   (map-click-evt e)))}]}]])]]])))


(defn mapping-table-row [{:keys [required? id multi-field?]} label content]
  [:tr
   [:td.pr-3.py-1 {:class (when-not multi-field? "align-middle")}
    [:label.bx--label.font-semibold {:for   id
                                     :class (when required? "required-field")}
     label]]
   [:td.pr-3.py-1
    content]])


(defn config->attrs-to-map [config]
  (concat gis-config-model/required-mapping-keys
          (if (config-has-role? config :right-of-way-search)
            [:address/street-number
             :address/street-name
             :property/description]
            gis-config-model/optional-mapping-keys)))


(defn gis-attribute-mapping [{:keys [tr
                                     gis-attribute-names
                                     server-id
                                     search-config-id
                                     set-editor-val
                                     config
                                     read-only-attributes
                                     attribute-types]}]
  (let [mapping      (:gis-search-config/mapping config {})
        attrs-to-map (config->attrs-to-map config)]
    [:div
     [:h6.mb-3 (tr [:gis-search-config/mapping])]
     [:p (tr [:gis-search-config/mapping-guide-1])]
     [:p (tr [:gis-search-config/mapping-guide-2])]
     [:table
      (-> [:tbody]
          (into
            (for [attr attrs-to-map
                  :let [id        (str/join "-" [server-id search-config-id "mapping" attr])
                        required? (contains? (set gis-config-model/required-mapping-keys) attr)]]
              [mapping-table-row
               (util/mmap required? id)
               (tr [(keyword "property-gis-admin" (name attr)) attr])
               [ui-select/select {:id           id
                                  :class-name   "full-width-select"
                                  :select-text  (tr [:gis-editor/select-attribute])
                                  :label-text   (tr [:gis-editor/gis-attribute])
                                  :hide-label   true
                                  :value        (let [entry (attr mapping)]
                                                  (gis-config-model/mapping-name entry))
                                  :on-change    (fn [gis-attr]
                                                  (let [entry (if-let [attr-type (get attribute-types gis-attr)]
                                                                [gis-attr {:type attr-type}]
                                                                gis-attr)]
                                                    (set-editor-val {:gis-search-config/mapping {attr entry}})))
                                  :val->str     identity
                                  :allow-empty? true
                                  :values       gis-attribute-names
                                  :disabled     (boolean (or (and (#{:address/street-number :address/street-name} attr)
                                                                  (:address/full-address mapping))
                                                             (and (= :address/full-address attr)
                                                                  (some mapping [:address/street-number
                                                                                 :address/street-name]))
                                                             (contains? read-only-attributes attr)))}]]))
          (into [[mapping-table-row
                  {:id "addr-lang" :required? false}
                  (tr [:address/language])
                  (let [languages (<sub [::i18n/locales])]
                    [ui-select/select {:id           "addr-lang"
                                       :class-name   "full-width-select"
                                       :select-text  (tr [:gis-editor/select-attribute])
                                       :label-text   (tr [:gis-editor/gis-attribute])
                                       :hide-label   true
                                       :value        (:address/language mapping)
                                       :on-change    (fn [gis-attr]
                                                       (set-editor-val {:gis-search-config/mapping
                                                                        {:address/language gis-attr}}))
                                       :val->str     (fn [lang]
                                                       (-> languages (get lang) :name))
                                       :allow-empty? true
                                       :values       (map first languages)
                                       :disabled     (nil? (:address/full-address mapping))}])]]))]

     (when (seq (:group-ids config))
       [municipality-mapping
        (util/mmap tr server-id search-config-id gis-attribute-names attribute-types)
        config])]))


(defn property-search-config [{:keys [tr
                                      role
                                      gis-attribute-names
                                      server-id
                                      search-config-id
                                      set-editor-val
                                      config
                                      attribute-types
                                      read-only-attributes]}]
  (when (config-has-role? config role)
    (let [role-config (get-in config [:gis-search-config/property-search-config role] {})]
      [:div.mt-6
       [:h6.mb-3 (tr [(keyword "gis-search-config.role" (name role))])]
       [:p (tr [(keyword "gis-search-config" (str (name role) "-guide"))])]
       [:table
        (cond-> [:tbody]
          (= role :property-owner-search)
          (into
            (for [attr gis-config-model/property-owner-search-mapping-keys
                  :let [id        (str/join "-" [server-id search-config-id "mapping" attr])
                        editors   (-> role-config :attributes attr count inc)
                        required? (= attr :name)]]
              [mapping-table-row
               (assoc (util/mmap required? id) :multi-field? true)
               (tr [(keyword "gis-search-config" (str/join "-" ["property-owner" (name attr)]))])

               [:div
                (for [editor-idx (range editors)]
                  ^{:key (str/join "-" [id editor-idx])}
                  [ui-select/select {:id           (str/join "-" [id editor-idx])
                                     :class-name   "full-width-select py-1"
                                     :select-text  (tr [:gis-editor/select-attribute])
                                     :label-text   (tr [:gis-editor/gis-attribute])
                                     :hide-label   true
                                     :value        (-> role-config :attributes attr (get editor-idx))
                                     :on-change    (fn [gis-attr]
                                                     (let [curr-values (-> role-config :attributes attr)
                                                           new-values  (->> (if (seq curr-values)
                                                                              (assoc curr-values editor-idx gis-attr)
                                                                              [gis-attr])
                                                                            (remove nil?)
                                                                            vec)]
                                                       (set-editor-val {:gis-search-config/property-search-config
                                                                        {role
                                                                         {:attributes
                                                                          (when (seq new-values) {attr new-values})}}})))
                                     :val->str     identity
                                     :allow-empty? true
                                     :values       gis-attribute-names
                                     :disabled     (boolean (or (and (= :address attr)
                                                                     (-> role-config :attributes :address-multifield))
                                                                (and (= :address-multifield attr)
                                                                     (-> role-config :attributes :address))
                                                                (contains? read-only-attributes attr)))}])]]))
          :always
          (into
            [[mapping-table-row
              {:required? true
               :id        (str/join "-" [server-id search-config-id "id-attribute"])}
              (tr [:gis-search-config/id-attribute])
              [ui-select/select {:id           "id-attribute-select"
                                 :class-name   "full-width-select"
                                 :select-text  (tr [:gis-editor/select-attribute])
                                 :label-text   (tr [:gis-editor/gis-attribute])
                                 :hide-label   true
                                 :value        (-> role-config :id-attribute)
                                 :on-change    (fn [gis-attr]
                                                 (set-editor-val {:gis-search-config/property-search-config
                                                                  {role
                                                                   (p/assoc-if {:id-attribute gis-attr}
                                                                               :id-type (-> attribute-types (get gis-attr)))}}))
                                 :val->str     identity
                                 :allow-empty? true
                                 :values       gis-attribute-names
                                 :disabled     (contains? read-only-attributes :id-attribute)}]]]))]])))


(defn custom-config-warning [tr group-ids]
  (when (<sub [::gis-subs/groups-with-custom-configs group-ids])
    [:div.my-2.text-yellow-600.font-bold
     (cc/icon {:icon        i/warning
               :description (tr [:gis-search-config/custom-config-warning])
               :height      "15px"
               :width       "15px"
               :style       {:fill "currentcolor"}})
     [:span.ml-1 (tr [:gis-search-config/custom-config-warning])]]))


(defn config-name-input [tr config set-editor-val]
  [ui/text-input {:label-text (tr [:gis-search-config/name])
                  :value      (:gis-search-config/name config)
                  :on-change  #(set-editor-val {:gis-search-config/name (futil/tval %)})}])

(defn render-on-map-checkbox [tr config server-id search-config-id set-editor-val]
  (cc/checkbox {:id         (str/join "-" [server-id search-config-id "render-on-map?"])
                :label-text (tr [:gis-search-config/render-on-map?])
                :checked    (boolean (:gis-search-config/render-on-map? config))
                :on-change  #(set-editor-val
                               {:gis-search-config/render-on-map? %})}))


(defn searchable-checkbox [tr config server-id search-config-id set-editor-val]
  (cc/checkbox {:id         (str/join "-" [server-id search-config-id "searchable?"])
                :label-text (tr [:gis-search-config/searchable?])
                :checked    (not (false? (:gis-search-config/searchable? config)))
                :on-change  #(set-editor-val
                               {:gis-search-config/searchable? %})}))


(defn role-selector [tr config server-config search-config-id set-editor-val]
  (cc/form-group {:legend-text (tr [:gis-search-config/roles])
                  :class-name  "my-3"}
                 (for-frag [role (roles-for-system (:gis-server-config/system server-config))]
                   (let [current-roles            (-> config :gis-search-config/roles set)
                         wms-wfs-property-config? (and (gis-common/wms-wfs-server? server-config) (= :property-owner-search role))]
                     (cc/checkbox {:id         (str (:db/id server-config) "-" search-config-id "-" role)
                                   :label-text (tr [(keyword "gis-search-config.role" role)])
                                   :checked    (contains? current-roles role)
                                   :on-change  (fn [checked?]
                                                 (let [new-roles-config {:gis-search-config/roles ((if checked? conj disj) current-roles role)}]
                                                   (set-editor-val
                                                     (cond
                                                       (and wms-wfs-property-config? checked?)
                                                       (assoc new-roles-config :gis-search-config/property-search-config
                                                              (assoc (:gis-search-config/property-search-config config)
                                                                     :property-owner-search
                                                                     gis-config-model/wfs-constant-property-search-mappings))

                                                       (and wms-wfs-property-config? (not checked?))
                                                       (assoc new-roles-config :gis-search-config/property-search-config
                                                              (dissoc (:gis-search-config/property-search-config config) role))

                                                       (and checked? (#{:property-owner-search :neighbour-search} role))
                                                       (merge new-roles-config {:gis-search-config/property-search-config {role {}}})

                                                       (and (not checked?) (#{:property-owner-search :neighbour-search} role))
                                                       (update new-roles-config :gis-search-config/property-search-config #(dissoc (or % {}) role))

                                                       :else
                                                       new-roles-config))))})))))


(defn group-selector [tr config server-id search-config-id]
  (let [domain-groups (<sub [::admin-common/domain-groups-of-authorities])]
    [:div.w-full.lg:w-3-4
     [:h6.mb-2 (tr [:gis-editor/groups-using-config])]
     [admin-common/member-group-editor {:tr                tr
                                        :groups            domain-groups
                                        :current-group-ids (:group-ids config)
                                        :add-title         (tr [:gis-editor/add-group])
                                        :save-evt          [::gis-common/add-domain-group
                                                            ::search-config-editors
                                                            server-id
                                                            search-config-id]
                                        :remove-evt        [::gis-common/remove-domain-group
                                                            ::search-config-editors
                                                            server-id
                                                            search-config-id]}]
     [custom-config-warning tr (:group-ids config)]]))


(defn search-config-save-button [{:keys [tr config server-id search-config-id command invalid-config?]}]
  (cc/button {:disabled (or invalid-config?
                            (<sub [::gis-common/save-in-progress?]))
              :on-click #(>evt [::gis-common/save-config
                                command
                                ::search-config-editors
                                server-id
                                search-config-id
                                config])}
             (tr [:save])))


(defn search-config-cancel-button [{:keys [tr search-config-id server-id]}]
  (cc/button {:kind       :ghost
              :class-name "mr-2"
              :on-click   #(>evt [::close-config-editor server-id search-config-id])}
             (tr [:cancel])))


(defn search-config-remove-button [{:keys [tr config search-config-id]}]
  (when (not= search-config-id :new)
    [ui/modal-confirmation-button {:tr                  tr
                                   :trigger-button-text (tr [:remove])
                                   :trigger-button-opts {:kind       :danger
                                                         :class-name "mr-2"}
                                   :on-yes-click        #(>evt [::gis-common/remove-config
                                                                :admin/remove-search-config
                                                                search-config-id])}
     (tr [:gis-editor/confirm-removal]
         [(:gis-search-config/name config)])]))


(defn sample-records-display-generic [{:keys [tr
                                              samples
                                              municipalities
                                              map-click-evt
                                              server-id
                                              search-config-id
                                              map-initially-collapsed?]}]
  (r/with-let [*sample-ix (r/atom 0)]
    (let [sample         (if (-> samples count (> @*sample-ix))
                           (nth samples @*sample-ix)
                           (first samples))
          gis-attributes (gis-common/get-attributes sample)]
      [:div.w-full
       [:h6.mb-3 (tr [:gis-editor/sample-record])]
       (when (next samples)
         [:<>
          [:p (tr [:gis-editor/multiple-records-help])]
          [:span.mr-2 (tr [:gis-editor/select-record])]
          [ui-cs/content-switcher
           {:items     (-> samples count range)
            :class     "mt-2 mb-4"
            :current   @*sample-ix
            :on-change #(reset! *sample-ix %)}]])
       [sample-record-map (util/mmap {:initially-collapsed? map-initially-collapsed?}
                                     municipalities
                                     map-click-evt
                                     tr
                                     server-id
                                     search-config-id
                                     sample)]
       [sample-attributes tr gis-attributes sample]])))


(defn sample-records-display [{:keys [tr
                                      server-id
                                      search-config-id
                                      selected-folder
                                      selected-service
                                      selected-layer-id
                                      query-type]}]
  (let [samples (<sub [::gis-common/arcgis-sample-records
                       server-id
                       selected-folder
                       selected-service
                       selected-layer-id])]
    [:div.w-full.lg:w-1-2.lg:pl-2
     [sample-records-display-generic (util/mmap {:municipalities (<sub [::relevant-municipalities-with-center server-id search-config-id])
                                                 :map-click-evt  (fn [^js e]
                                                                   (>evt [::gis-common/fetch-arcgis-sample
                                                                          server-id
                                                                          selected-folder
                                                                          selected-service
                                                                          selected-layer-id
                                                                          query-type
                                                                          (.-coordinate e)]))}
                                                tr
                                                server-id
                                                search-config-id
                                                samples)]]))


(defn arcgis-search-config-editor [tr server-config search-config-id]
  (let [server-id         (:db/id server-config)
        config            (<sub [::config-in-editor server-id search-config-id])
        render-on-map?    (<sub [::ff/enabled? :feature-flag.id/display-vector-layers-on-map])
        selected-folder   (:gis-service-config/arcgis-folder config)
        selected-service  (-> (select-keys config [:gis-service-config/arcgis-service
                                                   :gis-service-config/arcgis-service-type])
                              not-empty)
        selected-layer-id (:gis-search-config/arcgis-layer-id config)
        invalid-config?   (boolean ((arcgis-search-config-checker config) config))
        set-editor-val    (fn [m]
                            (rf/dispatch-sync [::gis-common/set-editor-value
                                               ::search-config-editors
                                               server-id
                                               search-config-id
                                               m]))
        selector-props    (util/mmap tr
                                     server-config
                                     search-config-id
                                     selected-folder
                                     selected-service
                                     selected-layer-id
                                     set-editor-val)
        query-type        (if (config-has-role? config :right-of-way-search)
                            :point-distance-query
                            :point-query)]
    [:div.mt-4.p-4.bg-ui-white.rounded
     [:div.w-full.lg:w-1-2
      [config-name-input tr config set-editor-val]
      (when-not (config-has-role? config :right-of-way-search)
        [role-selector tr config server-config search-config-id set-editor-val])
      [group-selector tr config server-id search-config-id]
      (when (and render-on-map?
                 (-> server-config
                     :gis-server-config/system
                     (not= :regrid-arcgis)))
        [render-on-map-checkbox tr config server-id search-config-id set-editor-val])
      (when (config-has-role? config :right-of-way-search)
        [searchable-checkbox tr config server-id search-config-id set-editor-val])

      [arcgis-search-config/instant-configure selector-props]
      [arcgis-search-config/folder-selector selector-props]
      [arcgis-search-config/service-selector selector-props]
      [arcgis-search-config/layer-selector selector-props]]
     (if (<sub [::gis-common/arcgis-samples-loaded?
                server-id
                selected-folder
                selected-service
                selected-layer-id])
       (let [gis-attribute-names (<sub [::gis-common/arcgis-attribute-names
                                        server-id
                                        selected-folder
                                        selected-service
                                        selected-layer-id])
             attribute-types     (<sub [::gis-common/arcgis-attribute-types
                                        server-id
                                        selected-folder
                                        selected-service
                                        selected-layer-id])
             editor-common-props (util/mmap tr
                                            gis-attribute-names
                                            server-id
                                            search-config-id
                                            set-editor-val
                                            config
                                            attribute-types)]
         [:<>
          [:div.mt-6.flex.flex-wrap
           [:div.w-full.lg:w-1-2.lg:pr-2
            (when (config-has-role? config :address-search :point-search :right-of-way-search)
              [gis-attribute-mapping editor-common-props])
            (when (config-has-role? config :property-owner-search)
              [property-search-config (assoc editor-common-props :role :property-owner-search)])
            (when (config-has-role? config :neighbour-search)
              [property-search-config (assoc editor-common-props :role :neighbour-search)])]
           [sample-records-display (util/mmap tr
                                              server-id
                                              search-config-id
                                              selected-folder
                                              selected-service
                                              selected-layer-id
                                              query-type)]]])

       (when selected-layer-id
         [:div.mt-6
          (cc/inline-loading {:description (tr [:gis-editor/retrieving-sample])})]))

     [:div.flex.justify-end.mt-6
      [search-config-remove-button (util/mmap tr config search-config-id)]
      [search-config-cancel-button (util/mmap tr search-config-id server-id)]
      [search-config-save-button (merge (util/mmap tr
                                                   config
                                                   search-config-id
                                                   server-id
                                                   invalid-config?)
                                        {:command :admin/save-arcgis-search-config})]]]))


(defn cgis-search-config-editor [tr server-config search-config-id]
  (let [server-id       (:db/id server-config)
        server-data     (<sub [::gis-common/cgis-service-info server-id])
        config          (<sub [::config-in-editor server-id search-config-id])
        invalid-config? (boolean (cgis-search-config-checker config))
        set-editor-val  (fn [m]
                          (>evt [::gis-common/set-editor-value
                                 ::search-config-editors
                                 server-id
                                 search-config-id
                                 m]))]
    [:div.mt-4.p-4.bg-ui-white.rounded
     [:div.w-full.lg:w-1-2
      [config-name-input tr config set-editor-val]
      [role-selector tr config server-config search-config-id set-editor-val]
      [group-selector tr config server-id search-config-id]]

     (if-let [sample (:sample-record server-data)]
       (let [gis-attributes      (->> sample
                                      (p/map-keys name)
                                      (sort-by first))
             gis-attribute-names (map first gis-attributes)
             editor-common-props (util/mmap tr
                                            gis-attribute-names
                                            server-id
                                            search-config-id
                                            set-editor-val
                                            config)]
         [:<>
          [:div.mt-6.flex.flex-wrap
           [:div.w-full.lg:w-1-2.lg:pr-2
            (when (config-has-role? config :address-search :point-search)
              [gis-attribute-mapping editor-common-props])
            (when (config-has-role? config :property-owner-search)
              [property-search-config (assoc editor-common-props :role :property-owner-search)])]
           [:div.w-full.lg:w-1-2.lg:pl-2
            [:h6.mb-3 (tr [:gis-editor/sample-record])]
            [sample-attributes tr gis-attributes sample]]]])
       [:div.mt-6
        (cc/inline-loading {:description (tr [:gis-editor/retrieving-sample])})])

     [:div.flex.justify-end.mt-6
      [search-config-remove-button (util/mmap tr config search-config-id)]
      [search-config-cancel-button (util/mmap tr search-config-id server-id)]
      [search-config-save-button (merge (util/mmap tr
                                                   config
                                                   search-config-id
                                                   server-id
                                                   invalid-config?)
                                        {:command :admin/save-cgis-search-config})]]]))


(defn wfs-search-config-editor [tr server-config search-config-id]
  (let [server-id       (:db/id server-config)
        config          (<sub [::config-in-editor server-id search-config-id])
        capabilities    (<sub [::wfs-capabilities server-id search-config-id])
        invalid-config? (boolean (wfs-search-config-checker config))
        set-editor-val  (fn [m]
                          (rf/dispatch-sync [::gis-common/set-editor-value
                                             ::search-config-editors
                                             server-id
                                             search-config-id
                                             m]))
        feature-sample  (<sub [::wfs-feature-sample server-id search-config-id])]
    [:div.mt-4.p-4.bg-ui-white.rounded
     [:div.w-full.lg:w-1-2
      [config-name-input tr config set-editor-val]
      [role-selector tr config server-config search-config-id set-editor-val]
      [group-selector tr config server-id search-config-id]
      [:div.mt-4
       (cc/checkbox {:id         (str server-id "-" search-config-id "-wfs-swap-coordinates")
                     :label-text (tr [:gis-search-config/wfs-swap-coordinates?])
                     :checked    (boolean (:gis-search-config/wfs-swap-coordinates? config))
                     :on-change  #(set-editor-val {:gis-search-config/wfs-swap-coordinates? %})})]]

     [:div.mt-4
      [:h6.mb-3 (tr [:gis-search-config/wfs-path-and-feature])]
      [:div
       [ui/text-input {:label-text (tr [:gis-search-config/wfs-path])
                       :value      (or (:gis-search-config/wfs-path config) "")
                       :on-change  #(do
                                      (set-editor-val {:gis-search-config/wfs-path         (futil/tval %)
                                                       :gis-search-config/wfs-feature-name nil})
                                      (>evt [::debounce-fetch-wfs-capabilities
                                             server-id
                                             (futil/tval %)
                                             search-config-id]))}]]
      [:p.pt-2.pb-0
       (str (tr [:gis-search-config/wfs-full-path])
            ": "
            (gis-util/wms-wfs-service-full-path (:gis-server-config/service-url server-config)
                                                (:gis-search-config/wfs-path config)))]]

     (when (:loading capabilities)
       [:div.mt-6
        (cc/inline-loading {:description (tr [:gis-search-config/retrieving-wfs-capabilities])})])

     (when-let [error (:error capabilities)]
       [:p.pt-2.pb-0.font-bold.text-ui-red
        (tr [:gis-search-config/wfs-server-error])
        " "
        (case (:status error)
          401 (tr [:gis-editor/check-credentials])
          404 (tr [:gis-editor/check-path])
          (tr [:gis-editor/unknown-error]))])

     (when-let [service-version (:service-version capabilities)]
       [:p.pt-2.pb-0
        (str (tr [:gis-search-config/wfs-version])
             ": "
             service-version)])

     (when-let [features (:features capabilities)]
       (let [name->feature (zipmap (map :name features) features)]
         [:div.mt-3
          (when (seq features)
            [ui-select/select
             {:id           (str/join "-" [server-id search-config-id "feature-select"])
              :class-name   "full-width-select"
              :label-text   (tr [:gis-search-config/wfs-feature])
              :select-text  (tr [:gis-search-config/select-wfs-feature])
              :val->str     (fn [val]
                              (let [{:keys [name title]} (get name->feature val)]
                                (str name (when title (str " (" title ")")))))
              :allow-empty? false
              :value        (:gis-search-config/wfs-feature-name config)
              :values       (sort (keys name->feature))
              :on-change    (fn [name]
                              (set-editor-val {:gis-search-config/wfs-feature-name name})
                              (>evt [::fetch-wfs-sample
                                     server-id
                                     search-config-id
                                     (:gis-search-config/wfs-version config)
                                     (:gis-search-config/wfs-path config)
                                     name]))}])]))

     (when (:loading feature-sample)
       [:div.mt-6
        (cc/inline-loading {:description (tr [:gis-editor/retrieving-sample])})])

     (when (:error feature-sample)
       [:p.pt-2.pb-0.font-bold.text-ui-red
        (tr [:gis-search-config/wfs-sample-error])])

     (when-let [feature (:feature feature-sample)]
       (let [gis-attributes       (->> feature
                                       (p/map-keys name)
                                       (sort-by first))
             gis-attribute-names  (map first gis-attributes)
             read-only-attributes (merge gis-config-model/wfs-constant-attribute-mappings
                                         gis-config-model/wfs-constant-property-search-mappings)
             editor-common-props  (util/mmap tr
                                             gis-attribute-names
                                             server-id
                                             search-config-id
                                             set-editor-val
                                             config
                                             read-only-attributes)]
         [:<>
          [:p.pt-2.pb-0
           (tr [:gis-search-config/wfs-feature-crs])
           ": "
           (:crs-name feature-sample)]
          [:div.mt-6.flex.flex-wrap
           [:div.w-full.lg:w-1-2.lg:pr-2
            (when (config-has-role? config :address-search :point-search)
              [gis-attribute-mapping editor-common-props])
            (when (config-has-role? config :property-owner-search)
              [property-search-config (assoc editor-common-props :role :property-owner-search)])]
           [:div.w-full.lg:w-1-2.lg:pl-2
            [:h6.mb-3 (tr [:gis-editor/sample-record])]
            [sample-attributes tr gis-attributes feature]]]]))

     [:div.flex.justify-end.mt-6
      [search-config-remove-button (util/mmap tr config search-config-id)]
      [search-config-cancel-button (util/mmap tr search-config-id server-id)]
      [search-config-save-button (merge (util/mmap tr
                                                   config
                                                   search-config-id
                                                   server-id
                                                   invalid-config?)
                                        {:command :admin/save-wfs-search-config})]]]))


(defn search-config-editor [tr server-config search-config-id]
  (case (:gis-server-config/system server-config)
    :cgis [cgis-search-config-editor tr server-config search-config-id]
    :wms-wfs [wfs-search-config-editor tr server-config search-config-id]
    [arcgis-search-config-editor tr server-config search-config-id]))


(defn gis-config-list
  [{:keys [tr server-config arcgis? title add-new-fn new? get-search-configs edit-fn]}]
  [:div.mt-4
   [:div.flex.justify-between.items-center.my-3
    [:h4 title]
    (cc/button {:size     :small
                :icon     i/plus
                :kind     :tertiary
                :on-click add-new-fn}
               (tr [:gis-editor/add-new]))]

   (when new?
     [search-config-editor tr server-config :new])

   (for-frag [{:gis-search-config/keys [name roles mapping municipality-filters property-search-config] :as search-config} (get-search-configs server-config)
              :let [domain-groups    (:group/_gis-search-configs search-config)
                    server-id        (:db/id server-config)
                    search-config-id (:db/id search-config)]]
     (if (<sub [::config-in-editor server-id search-config-id])
       [search-config-editor tr server-config search-config-id]
       [:div.mt-4.p-4.bg-ui-white.rounded
        [:div.flex.justify-between.items-center
         [:h5.mb-2 name]
         (cc/button {:icon             i/pen
                     :size             :small
                     :kind             :tertiary
                     :icon-description ""
                     :on-click         (edit-fn search-config search-config-id domain-groups)}
                    (tr [:edit]))]
        [:div
         (for-frag [group domain-groups]
           [ui/tag {:type       :round
                    :color      :night-sky
                    :class-name "first:ml-0 cloudpermit--tag--round"}
            (:group/name group)])]
        [:div
         (for-frag [role roles]
           [role-tag tr role])]
        (when arcgis?
          [:div.flex.flex-wrap
           (for-frag [k [:gis-service-config/arcgis-folder
                         :gis-service-config/arcgis-service
                         :gis-service-config/arcgis-service-type
                         :gis-search-config/arcgis-layer-id]]
             [gis-common/form-item tr k (k search-config)])])
        (when (config-has-role? search-config :address-search :point-search :right-of-way-search)
          [:div.mt-4
           [:h6 (tr [:gis-search-config/mapping])]
           [:div.flex.flex-row.flex-wrap
            (for-frag [[k v] mapping]
              [gis-common/form-item tr k (gis-config-model/mapping-name v)])]])
        (when (config-has-role? search-config :property-owner-search)
          [:div.mt-4
           [:h6 (tr [:gis-search-config.role/property-owner-search])]
           [:div.flex.flex-row.flex-wrap
            (for-frag [[k v] (-> property-search-config :property-owner-search :attributes)]
              [gis-common/form-item tr (keyword "gis-search-config" (str/join "-" ["property-owner" (clojure.core/name k)])) (str/join ", " v)])]])
        (when (seq municipality-filters)
          [:div.mt-4
           [:div
            [:h6 (tr [:gis-search-config/municipality-filters])]
            [:div.flex.flex-row.flex-wrap
             (for-frag [filter municipality-filters]
               [:div.flex.flex-wrap.w-full
                (for-frag [[k v] (select-keys filter [:gis-municipality-filter/municipality-number
                                                      :gis-municipality-filter/attribute
                                                      :gis-municipality-filter/match-type
                                                      :gis-municipality-filter/value])]
                  [gis-common/form-item tr k v])])]]])]))])


(defn gis-search-config-list
  [tr server-config]
  (let [server-id (:db/id server-config)
        arcgis?   (gis-common/arcgis-server? server-config)
        wms-wfs?  (gis-common/wms-wfs-server? server-config)]
    [gis-config-list
     (util/mmap
       {:title              (tr [:gis-editor/search-configurations])
        :arcgis?            arcgis?
        :new?               (when-let [s (<sub [::config-in-editor server-id :new])] (not (config-has-role? s :right-of-way-search)))
        :add-new-fn         (fn [_]
                              (cond
                                wms-wfs?
                                (>evt [::fetch-wfs-capabilities
                                       server-id
                                       ""
                                       :new])
                                (gis-common/system-of-type? server-config :azimut-arcgis)
                                (>evt [::gis-common/fetch-azimut-arcgis-information
                                       server-config
                                       ::search-config-editors])
                                (gis-common/system-of-type? server-config :arcgis :cgis)
                                (>evt [::gis-common/fetch-arcgis-server-information
                                       server-config
                                       ""
                                       [::gis-common/arcgis-server-root-info-fetched server-id]]))
                              (>evt [::open-config-editor server-config {} #{}]))
        :get-search-configs (fn [server]
                              (->> server
                                   :gis-service-config/_gis-server-config
                                   (filter (eu/is-type-fn :entity.type/gis-search-config))
                                   (remove #(contains? (:gis-search-config/roles %) :right-of-way-search))
                                   (sort-by :gis-search-config/name)))
        :edit-fn            (fn [search-config search-config-id domain-groups]
                              (fn [_]
                                (if wms-wfs?
                                  (>evt [::fetch-wfs-capabilities
                                         server-id
                                         (:gis-search-config/wfs-path search-config)
                                         search-config-id
                                         [::fetch-wfs-sample
                                          server-id
                                          search-config-id
                                          (:gis-search-config/wfs-version search-config)
                                          (:gis-search-config/wfs-path search-config)
                                          (:gis-search-config/wfs-feature-name search-config)]])
                                  (gis-common/fetch-arcgis-server-info-for-edit server-config search-config))
                                (>evt [::open-config-editor
                                       server-config
                                       search-config
                                       (set (map :db/id domain-groups))])))} tr server-config)]))


(defn gis-row-config-list [tr server-config]
  (let [server-id (:db/id server-config)
        arcgis?   (gis-common/arcgis-server? server-config)]
    [gis-config-list
     (util/mmap
       {:arcgis?            arcgis?
        :title              (tr [:gis-editor/row-configurations])
        :new?               (when-let [s (<sub [::config-in-editor server-id :new])] (config-has-role? s :right-of-way-search))
        :add-new-fn         (fn [_]
                              (>evt [::gis-common/fetch-arcgis-server-information
                                     server-config
                                     ""
                                     [::gis-common/arcgis-server-root-info-fetched server-id]])
                              (>evt [::open-config-editor server-config {:gis-search-config/roles #{:right-of-way-search}} #{}]))
        :get-search-configs (fn [server] (->> server
                                              :gis-service-config/_gis-server-config
                                              (filter (eu/is-type-fn :entity.type/gis-search-config))
                                              (filter #(contains? (:gis-search-config/roles %) :right-of-way-search))
                                              (sort-by :gis-search-config/name)))
        :edit-fn            (fn [search-config _ domain-groups]
                              (fn [_]
                                (gis-common/fetch-arcgis-server-info-for-edit server-config search-config)
                                (>evt [::open-config-editor
                                       server-config
                                       search-config
                                       (set (map :db/id domain-groups))])))} tr server-config)]))
